import React, { useMemo, useCallback } from 'react';
import { motion, LayoutGroup } from 'framer-motion';

interface SubOptionSelectorProps {
  mainOption: any;
  availableSubOptions: any[];
  selectedOption: string;
  onOptionSelect: (selection: string, price: string) => void;
  modifierName?: string;
  optionName?: string;
}

export const SubOptionSelector: React.FC<SubOptionSelectorProps> = ({
  mainOption,
  availableSubOptions,
  selectedOption,
  onOptionSelect,
  modifierName = '',
  optionName = ''
}) => {
  if (!mainOption || !Array.isArray(availableSubOptions)) return null;

  const options = useMemo(() => {
    const whole = {
      name: 'Whole',
      price: mainOption.price || '0',
      isEnabled: true
    };

    const validSubs = availableSubOptions.filter(
      o => o?.name && o?.price !== undefined && o?.isEnabled
    );

    return [...validSubs, whole];
  }, [availableSubOptions, mainOption]);

  if (!options.length) return null;

  const renderIcon = useCallback((name: string) => {
    if (name === 'Whole') {
      return (
        <svg aria-hidden className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" clipRule="evenodd" d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM7 5C7.55228 5 8 4.55228 8 4C8 3.44772 7.55228 3 7 3C6.44772 3 6 3.44772 6 4C6 4.55228 6.44772 5 7 5ZM13 8C14.1046 8 15 7.10457 15 6C15 4.89543 14.1046 4 13 4C11.8954 4 11 4.89543 11 6C11 7.10457 11.8954 8 13 8ZM16 12C16.5523 12 17 11.5523 17 11C17 10.4477 16.5523 10 16 10C15.4477 10 15 10.4477 15 11C15 11.5523 15.4477 12 16 12ZM11 11C11 11.5523 10.5523 12 10 12C9.44772 12 9 11.5523 9 11C9 10.4477 9.44772 10 10 10C10.5523 10 11 10.4477 11 11ZM9 15C9 15.5523 8.55228 16 8 16C7.44772 16 7 15.5523 7 15C7 14.4477 7.44772 14 8 14C8.55228 14 9 14.4477 9 15ZM13.5 17C14.3284 17 15 16.3284 15 15.5C15 14.6716 14.3284 14 13.5 14C12.6716 14 12 14.6716 12 15.5C12 16.3284 12.6716 17 13.5 17ZM6 8.5C6 9.32843 5.32843 10 4.5 10C3.67157 10 3 9.32843 3 8.5C3 7.67157 3.67157 7 4.5 7C5.32843 7 6 7.67157 6 8.5Z" fill="currentColor" />
        </svg>
      );
    }

    const isLeft = name.toLowerCase().includes('left');

    if (isLeft) {
      // Left Half - Filled left + outline right
      return (
        <svg aria-hidden className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" clipRule="evenodd" d="M10 1.99999C10 0.895421 9.09555 -0.0208739 8.01277 0.197443C3.44193 1.11904 0 5.15756 0 9.99999C0 14.8424 3.44193 18.8809 8.01277 19.8025C9.09555 20.0209 10 19.1046 10 18V12C9.44772 12 9 11.5523 9 11C9 10.4477 9.44772 10 10 10V1.99999ZM8 4C8 4.55228 7.55228 5 7 5C6.44772 5 6 4.55228 6 4C6 3.44772 6.44772 3 7 3C7.55228 3 8 3.44772 8 4ZM8 16C8.55228 16 9 15.5523 9 15C9 14.4477 8.55228 14 8 14C7.44772 14 7 14.4477 7 15C7 15.5523 7.44772 16 8 16ZM4.5 10C5.32843 10 6 9.32843 6 8.5C6 7.67157 5.32843 7 4.5 7C3.67157 7 3 7.67157 3 8.5C3 9.32843 3.67157 10 4.5 10Z" fill="currentColor" />
          <path d="M20 10C20 14.838 16.5645 18.8735 12 19.8V17.748C15.4505 16.8599 18 13.7277 18 10C18 6.27236 15.4505 3.14016 12 2.25207V0.200073C16.5645 1.12661 20 5.16212 20 10Z" fill="currentColor" />
        </svg>
      );
    }

    // Right Half - Filled right + outline left
    return (
      <svg aria-hidden className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 21 20" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M10.5 18C10.5 19.1046 11.4045 20.0209 12.4872 19.8025C17.0581 18.8809 20.5 14.8424 20.5 9.99999C20.5 5.15756 17.0581 1.11904 12.4872 0.197442C11.4045 -0.0208738 10.5 0.895421 10.5 1.99999L10.5 10C11.0523 10 11.5 10.4477 11.5 11C11.5 11.5523 11.0523 12 10.5 12L10.5 18ZM15.5 6.00001C15.5 7.10458 14.6046 8.00001 13.5 8.00001C12.3954 8.00001 11.5 7.10458 11.5 6.00001C11.5 4.89544 12.3954 4.00001 13.5 4.00001C14.6046 4.00001 15.5 4.89544 15.5 6.00001ZM17.5 11C17.5 11.5523 17.0523 12 16.5 12C15.9477 12 15.5 11.5523 15.5 11C15.5 10.4477 15.9477 10 16.5 10C17.0523 10 17.5 10.4477 17.5 11ZM15.5 15.5C15.5 16.3284 14.8284 17 14 17C13.1716 17 12.5 16.3284 12.5 15.5C12.5 14.6716 13.1716 14 14 14C14.8284 14 15.5 14.6716 15.5 15.5Z" fill="currentColor" />
        <path d="M0.5 9.99998C0.5 5.16206 3.93552 1.12655 8.5 0.200012L8.5 2.25201C5.04954 3.1401 2.5 6.2723 2.5 9.99998C2.5 13.7277 5.04955 16.8599 8.5 17.7479V19.7999C3.93552 18.8734 0.5 14.8379 0.5 9.99998Z" fill="currentColor" />
      </svg>
    );
  }, []);

  return (
    <LayoutGroup>
      <div className="mt-3">
        <div className="relative flex bg-white rounded-lg p-1 border border-gray-200 overflow-hidden">
          {options.map(option => {
            const isActive = selectedOption === option.name;

            return (
              <button
                key={option.name}
                onClick={() =>
                  onOptionSelect(option.name, String(option.price))
                }
                className="relative flex-1 z-10"
              >
                {/* Sliding Indicator */}
                {isActive && (
                  <motion.div
                    layoutId={`sub-option-slider-${modifierName}-${optionName}`}
                    className="absolute inset-0 rounded-md"
                    style={{ backgroundColor: '#FB2B37' }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 35
                    }}
                  />
                )}

                {/* Button Content */}
                <div
                  className={`
                    relative z-10 flex items-center justify-center gap-1.5
                    px-2 py-2 sm:px-3
                    text-[11px] sm:text-sm font-medium
                    transition-colors
                    ${isActive ? 'text-white' : 'text-gray-700'}
                  `}
                >
                  {renderIcon(option.name)}
                  <span className="whitespace-nowrap">
                    {option.name}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </LayoutGroup>
  );
};

export default SubOptionSelector;
